import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Grid,
  Paper,
  Button,
  FormControl,
  Select,
  MenuItem,
  Collapse
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InfoIcon from '@mui/icons-material/Info';
import CancelIcon from '@mui/icons-material/Cancel';
import dayjs from 'dayjs';
import MiniMap from './MiniMap';
import { useLanguage } from '../../utils/i18n';

const DenseItem = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" alignItems="flex-start" py={0.6} borderBottom="1px solid #f0f0f0">
    <Typography variant="body2" color="text.secondary" sx={{ pr: 2, minWidth: 130 }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: 'right', wordBreak: 'break-word', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
      {React.isValidElement(value) ? (
        value
      ) : (
        <Typography variant="body2" fontWeight="600" color="text.primary">
          {value !== null && value !== undefined && value !== '' ? String(value) : '-'}
        </Typography>
      )}
    </Box>
  </Box>
);

const SectionPanel = ({ title, children }) => (
  <Box mb={2.5}>
    <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ textTransform: 'uppercase', mb: 1, letterSpacing: 0.5 }}>
      {title}
    </Typography>
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#ffffff' }}>
      {children}
    </Paper>
  </Box>
);

export default function WaypointDetailDialog({ open, onClose, dataList, initialIndex }) {
  const { t } = useLanguage();
  const [localIndex, setLocalIndex] = useState(0);
  const [stepSize, setStepSize] = useState(1);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    if (open && initialIndex !== undefined && initialIndex >= 0 && initialIndex < dataList.length) {
      setLocalIndex(initialIndex);
    }
  }, [open, initialIndex, dataList]);

  if (!open || !dataList || dataList.length === 0) return null;

  const row = dataList[localIndex];
  if (!row) return null;

  const totalData = dataList.length;
  const canGoPrev = localIndex > 0;
  const canGoNext = localIndex < totalData - 1;

  const handlePrev = () => {
    setLocalIndex((prev) => Math.max(0, prev - stepSize));
  };

  const handleNext = () => {
    setLocalIndex((prev) => Math.min(totalData - 1, prev + stepSize));
  };

  // Safe JSON parse for CANBUS
  let parsedCanbus = {};
  if (row.canbus) {
    try {
      const rawData = row.canbus.data || row.canbus;
      parsedCanbus = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
    } catch {
      parsedCanbus = { raw: String(row.canbus.data || row.canbus) };
    }
  }

  const formatDate = (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm:ss') : '-');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', height: '85vh', maxHeight: '85vh' }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#f8f9fa',
          borderBottom: '1px solid #eee',
          px: 3,
          py: 2
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              {t('reportModule.waypointDetails', 'Waypoint Details')} {row.license_plate && `- ${row.license_plate}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Navigation Toolbar */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mt={2}
          p={1}
          sx={{ bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #e0e0e0' }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIosNewIcon fontSize="small" />}
              onClick={handlePrev}
              disabled={!canGoPrev}
            >
              {t('reportModule.prev', 'Prev')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              endIcon={<ArrowForwardIosIcon fontSize="small" />}
              onClick={handleNext}
              disabled={!canGoNext}
            >
              {t('reportModule.next', 'Next')}
            </Button>

            <Box display="flex" alignItems="center" gap={1} ml={2}>
              <Typography variant="body2" color="text.secondary">
                {t('reportModule.step', 'Step')}:
              </Typography>
              <FormControl size="small">
                <Select
                  value={stepSize}
                  onChange={(e) => setStepSize(e.target.value)}
                  sx={{ height: 32, '& .MuiSelect-select': { py: 0.5 } }}
                >
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Typography variant="body2" fontWeight="bold" color="primary">
            {t('data', 'Data')} {localIndex + 1} {t('reports.of', 'of')} {totalData}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: '#fbfbfb', overflow: 'hidden' }}>
        <Grid container spacing={3} sx={{ height: '100%' }}>
          {/* LEFT SIDE: General Data */}
          <Grid item size={{ xs: 12, md: 8 }} sx={{ height: '100%', overflowY: 'auto', pr: 1 }}>
            <Grid container spacing={3}>
              {/* Column 1 */}
              <Grid item size={{ xs: 12, sm: 6 }}>
                <SectionPanel title={t('tireHistorical.generalInformation', 'General Information')}>
                  <DenseItem label={t('reports.dateSend', 'Date Send')} value={formatDate(row.date_send)} />
                  <DenseItem label={t('reports.wpTime', 'WP Time')} value={formatDate(row.wp_time)} />

                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" py={0.6} borderBottom="1px solid #f0f0f0">
                    <Typography variant="body2" color="text.secondary" sx={{ pr: 2, minWidth: 130 }}>
                      {t('reportModule.latLon', 'Lat, Lon')}
                    </Typography>
                    <Box display="flex" flexDirection="column" alignItems="flex-end">
                      {row.lat && row.lon ? (
                        <>
                          <a
                            href={`https://www.google.com/maps?q=${row.lat},${row.lon}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="600"
                              color="primary"
                              sx={{ '&:hover': { textDecoration: 'underline' } }}
                            >
                              {row.lat}, {row.lon}
                            </Typography>
                          </a>
                          <Button
                            size="small"
                            variant="text"
                            sx={{ p: 0, minWidth: 0, textTransform: 'none', mt: 0.5, fontSize: '0.75rem' }}
                            onClick={() => setShowMap(!showMap)}
                          >
                            {showMap ? t('reportModule.hideLocation', 'Hide Location') : t('reportModule.showLocation', 'Show Location')}
                          </Button>
                        </>
                      ) : (
                        <Typography variant="body2" fontWeight="600" color="text.primary">
                          -
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Collapse in={showMap} unmountOnExit>
                    <Box mt={1} mb={2} borderRadius={2} overflow="hidden" border="1px solid #e0e0e0">
                      <MiniMap lat={row.lat} lon={row.lon} angle={row.angle} />
                    </Box>
                  </Collapse>

                  <DenseItem label={t('reports.street', 'Street')} value={row.street} />
                  <DenseItem label={t('reports.geofenceName', 'Geofence Name')} value={row.geofence_name} />
                  <DenseItem label={t('reports.routeName', 'Route Name')} value={row.route_name} />
                  <DenseItem label={t('reports.pitName', 'PIT Name')} value={row.pool_name} />
                  <DenseItem label={t('reports.jettyName', 'Jetty Name')} value={row.transit_point_name} />
                </SectionPanel>

                <SectionPanel title={t('reportModule.vehicleMetrics', 'Vehicle Metrics')}>
                  <DenseItem label={t('reports.virtualOdo', 'Virtual Odo')} value={row.virtual_odo} />
                  <DenseItem label={t('reports.totalOdo', 'Total Odo')} value={row.total_odo} />
                  <DenseItem label={t('reports.powerVolt', 'Power Volt')} value={row.power_volt} />
                  <DenseItem label={t('reports.batteryVolt', 'Battery Volt')} value={row.batt_volt} />
                  <DenseItem label={t('reports.liter', 'Liter')} value={row.liter} />
                  <DenseItem label={t('reports.harshAcceleration', 'Harsh Acceleration')} value={row.harsh_acceleration} />
                  <DenseItem label={t('reports.harshBraking', 'Harsh Braking')} value={row.harsh_breaking} />
                  <DenseItem label={t('reports.deepSleep', 'Deep Sleep')} value={row.deep_sleep} />
                </SectionPanel>
              </Grid>

              {/* Column 2 */}
              <Grid item size={{ xs: 12, sm: 6 }}>
                <SectionPanel title={t('reportModule.telemetryStatus', 'Telemetry & Status')}>
                  <DenseItem label={t('reports.speed', 'Speed')} value={row.speed} />
                  <DenseItem label={t('reports.angle', 'Angle')} value={row.angle} />
                  <DenseItem label={t('reports.altitude', 'Altitude')} value={row.altitude} />
                  <DenseItem label={t('reports.movement', 'Movement')} value={row.movement} />
                  <DenseItem label={t('reports.satellites', 'Satellites')} value={row.satellites} />
                  <DenseItem label={t('reports.gsmSignal', 'GSM Signal')} value={row.gsm_signal} />
                  <DenseItem label={t('reports.acceleration', 'Acceleration')} value={row.acceleration} />
                  <DenseItem label={t('reports.rfid', 'RFID')} value={row.rfid} />
                </SectionPanel>
                <SectionPanel title={t('reportModule.digitalInputs', 'Digital Inputs (DIN)')}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <DenseItem
                      key={`din-${i}`}
                      label={`DIN ${i} (${row[`din${i}_desc`] || ''})`}
                      value={
                        row[`din${i}`] ? (
                          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        ) : (
                          <CancelIcon sx={{ fontSize: 18, color: 'error.main' }} />
                        )
                      }
                    />
                  ))}
                </SectionPanel>
              </Grid>
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <SectionPanel title={t('reportModule.analogInputs', 'Analog Inputs (AIN)')}>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Box key={`ain-${i}`} display="flex" alignItems="center" bgcolor="#f4f6f8" px={1.5} py={0.5} borderRadius={1.5}>
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                        {t('reportModule.ain', 'AIN')} {i}:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {row[`ain${i}`] ?? '-'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </SectionPanel>

              <SectionPanel title={t('reportModule.temperatures', 'Temperatures (Suhu)')}>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  <Box display="flex" alignItems="center" bgcolor="#fff3e0" px={1.5} py={0.5} borderRadius={1.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                      {t('reportModule.temp', 'Temp')} 1:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="warning.dark">
                      {row.temperature ?? '-'}
                    </Typography>
                  </Box>
                  {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <Box key={`temp-${i}`} display="flex" alignItems="center" bgcolor="#fff3e0" px={1.5} py={0.5} borderRadius={1.5}>
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                        {t('reportModule.temp', 'Temp')} {i + 1}:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="warning.dark">
                        {row[`temperature${i}`] ?? '-'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </SectionPanel>
            </Grid>
          </Grid>

          {/* RIGHT SIDE: CANBUS Data */}
          <Grid item size={{ xs: 12, md: 4 }} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" flexDirection="column" height="100%">
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                color="primary"
                sx={{ textTransform: 'uppercase', mb: 1, letterSpacing: 0.5 }}
              >
                {t('reports.canbusDetails', 'CANBUS Details')}
              </Typography>
              <Paper
                elevation={0}
                sx={{ p: 0, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#ffffff', flexGrow: 1, overflowY: 'auto' }}
              >
                <Box p={2}>
                  {Object.keys(parsedCanbus).length > 0 ? (
                    Object.entries(parsedCanbus).map(([key, val]) => <DenseItem key={key} label={key} value={val} />)
                  ) : (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic" align="center" mt={4}>
                      {t('reportModule.noCanbusData', 'No CANBUS data available.')}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
